// BookFragments — the Notes-scene landmark. Five floating small
// boxes (0.15 x 0.2 x 0.05) tilted at random angles, slowly bobbing.
// They evoke "pages torn from a notebook" drifting in space.

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

const FRAGMENT_COUNT = 5;

// Deterministic-ish per-fragment config so the look is stable.
type FragmentConfig = {
  offset: [number, number, number];
  rot: [number, number, number];
  color: string;
  spine: string;
};
const FRAGMENT_CONFIGS: FragmentConfig[] = [
  { offset: [0.0, 0.0, 0.0], rot: [0.15, 0.4, 0.1], color: '#f5f0e8', spine: '#b8a572' },
  { offset: [0.35, 0.15, -0.2], rot: [-0.2, 0.9, 0.3], color: '#ededed', spine: '#7fb89f' },
  { offset: [-0.3, 0.25, 0.1], rot: [0.3, -0.6, -0.2], color: '#f5f0e8', spine: '#b8a572' },
  { offset: [0.15, -0.2, 0.3], rot: [-0.1, 1.1, 0.2], color: '#ededed', spine: '#5a5a5a' },
  { offset: [-0.2, 0.05, -0.3], rot: [0.25, -0.3, -0.15], color: '#f5f0e8', spine: '#7fb89f' },
];

export interface BookFragmentsProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  isActive?: boolean;
  isHovered?: boolean;
  groupRef?: React.MutableRefObject<Group | null>;
}

export function BookFragments({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isActive = false,
  isHovered = false,
  groupRef,
}: BookFragmentsProps) {
  const meshRefs = useRef<(Mesh | null)[]>([]);
  const localGroupRef = useRef<Group | null>(null);
  const t0 = useRef(performance.now() / 1000);

  const combinedScale = useMemo(() => {
    const base = scale;
    const hover = isHovered ? 1.08 : 1;
    const active = isActive ? 1.12 : 1;
    return base * hover * active;
  }, [scale, isHovered, isActive]);

  useFrame(() => {
    const t = performance.now() / 1000 - t0.current;
    for (let i = 0; i < FRAGMENT_COUNT; i++) {
      const m = meshRefs.current[i];
      if (!m) continue;
      // Bob: small Y offset on a 2.4s cycle, phase-shifted per index.
      const bob = Math.sin(t * (2 * Math.PI) / 2.4 + i * 0.7) * 0.04;
      const cfg = FRAGMENT_CONFIGS[i];
      m.position.set(cfg.offset[0], cfg.offset[1] + bob, cfg.offset[2]);
    }
  });

  // Combine refs: parent may pass its own groupRef; we need to forward
  // to localGroupRef too. We do this by rendering an outer <group> and
  // letting the parent set its ref via the prop.
  return (
    <group
      ref={(node) => {
        localGroupRef.current = node;
        if (groupRef) groupRef.current = node;
      }}
      name="landmark-book-fragments"
      position={position}
      rotation={rotation}
      scale={combinedScale}
    >
      {FRAGMENT_CONFIGS.map((cfg, i) => (
        <mesh
          key={i}
          ref={(node) => {
            meshRefs.current[i] = node;
          }}
          name={`fragment-${i}`}
          position={cfg.offset as unknown as [number, number, number]}
          rotation={cfg.rot as unknown as [number, number, number]}
          userData={{ landmarkType: 'book_fragments', index: i }}
        >
          <boxGeometry args={[0.15, 0.2, 0.05]} />
          <meshStandardMaterial
            color={cfg.color}
            emissive={cfg.color}
            emissiveIntensity={isActive ? 0.2 : 0.08}
            transparent
            opacity={0.85}
            roughness={0.7}
            flatShading
          />
        </mesh>
      ))}

      {/* Spine accent — a thin gold/mint strip along one edge. */}
      {FRAGMENT_CONFIGS.map((cfg, i) => (
        <mesh
          key={`spine-${i}`}
          name={`fragment-spine-${i}`}
          position={[cfg.offset[0] - 0.075, cfg.offset[1], cfg.offset[2]] as unknown as [number, number, number]}
          rotation={[cfg.rot[0], cfg.rot[1], cfg.rot[2]] as unknown as [number, number, number]}
          raycast={() => null}
        >
          <boxGeometry args={[0.012, 0.2, 0.05]} />
          <meshStandardMaterial
            color={cfg.spine}
            emissive={cfg.spine}
            emissiveIntensity={0.15}
            roughness={0.6}
            flatShading
          />
        </mesh>
      ))}
    </group>
  );
}

export default BookFragments;
